import { useQueries } from '@tanstack/react-query'
import { toast } from 'sonner'
import z from 'zod'
import { useHandleMissingToken } from '~/features/storyous/hooks/useHandleMissingToken'

type NormalizedStock = { name: string; amount: number; store: 'mandarin' | 'wollem' }

const listResponseSchema = z.object({
  data: z.array(z.object({ name: z.string(), amount: z.number() })),
  nextPage: z.string().nullable(),
})

const getStockList = async ({
  token,
  query,
  firstResponse,
  stockList = [],
  nextMap = {},
  chosenMerchant,
}: {
  firstResponse: Response
  token: string
  query?: string
  stockList?: Array<NormalizedStock>
  nextMap?: Record<string, boolean>
  chosenMerchant: 'wollem' | 'mandarin'
}): Promise<Array<NormalizedStock> | null> => {
  const response = query
    ? await fetch(`/api/storyous_stock`, {
        method: 'POST',
        body: JSON.stringify({ token, query, chosenMerchant }),
      })
    : firstResponse

  const result = await response.json()
  if (!response.ok) {
    toast.error('Storyous stock could not be fetched.')

    console.error(result)
    throw result
  }

  if (response.status < 300) {
    const parsed = listResponseSchema.safeParse({
      ...result,
      data: result.data.filter((el: unknown) => {
        const parsed = z.object({ measure: z.string() }).safeParse(el)
        if (parsed.success) return parsed.data.measure === 'pcs'

        return false
      }),
    })
    if (!parsed.success) {
      console.warn(parsed.error)
      toast.error('Unexpected return from Storyous. Check console.')
      return null
    }

    const data = parsed.data.data.map((d) => ({
      ...d,
      store: chosenMerchant,
    }))
    if (parsed.data.nextPage && !nextMap[parsed.data.nextPage]) {
      return await getStockList({
        firstResponse,
        token,
        query: parsed.data.nextPage,
        stockList: [...stockList, ...data],
        nextMap: { ...nextMap, [parsed.data.nextPage]: true },
        chosenMerchant,
      })
    }

    return [...stockList, ...data]
  }
  console.error('Unexpected status from Storous', response)
  toast.error('Storyous request failed.')
  return null
}

export const useGetStockList = ({
  token,
  merchants,
}: {
  token: string | undefined
  merchants: Array<'wollem' | 'mandarin'>
}) => {
  const handleMissingToken = useHandleMissingToken()
  return useQueries({
    queries: merchants.map((chosenMerchant) => ({
      queryKey: ['get-stock-list', token, chosenMerchant],
      queryFn: async () => {
        if (!token) {
          toast.warning('Can not make the request. Auth token is missing')
          throw Error('Can not make the request. Auth token is missing')
        }

        const firstResponse = await fetch(`/api/storyous_stock`, {
          method: 'POST',
          body: JSON.stringify({ token, chosenMerchant }),
        })
        if (handleMissingToken(firstResponse)) return null

        return await getStockList({ token, firstResponse, chosenMerchant })
      },
      enabled: !!token,
      retry: 0,
    })),
  })
}
